import { Component, NgModule, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UserService } from '../../services/user/user.service';
import {
  DateExclusionRow,
  DateExclusionService,
} from '../../services/date-exclusion.service';
import { formatDateAsYmd } from '../../constants/delivery-date.constants';

const UPCOMING_DAYS = 7;

function monthDay(ymd: string) {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function plusDays(ymd: string, days: number) {
  const [y, m, d] = ymd.split('-').map(Number);
  return formatDateAsYmd(new Date(y, m - 1, d + days));
}

export function isBlackoutActive(row: DateExclusionRow, todayYmd: string) {
  return (
    row.exclusion_start_date <= todayYmd && todayYmd <= row.exclusion_end_date
  );
}

export function blackoutLabel(row: DateExclusionRow, todayYmd: string) {
  const seller = row.seller_company_name || 'This seller';
  if (isBlackoutActive(row, todayYmd)) {
    return `${seller} is not accepting orders until ${monthDay(plusDays(row.exclusion_end_date, 1))}.`;
  }
  return `${seller} will not accept orders ${monthDay(row.exclusion_start_date)}–${monthDay(row.exclusion_end_date)}.`;
}

@Component({
  selector: 'app-ordering-blackout-banner',
  templateUrl: './ordering-blackout-banner.component.html',
})
export class OrderingBlackoutBannerComponent implements OnInit {
  lines: { text: string; active: boolean }[] = [];

  constructor(
    private userService: UserService,
    private dateExclusionService: DateExclusionService,
  ) {}

  ngOnInit() {
    // Fail open: runs on every buyer app load, so errors stay silent (no toast, no Sentry).
    this.userService.getMe().subscribe({
      next: res => {
        const user = res.data;
        // Anyone who places orders: buyers, and the reps who order on their behalf.
        const canOrder =
          !!user.roles?.includes('Buyer') ||
          !!user.is_sales_representative ||
          !!user.is_customer_representative;
        if (canOrder) {
          this.load();
        }
      },
      error: () => {
        this.lines = [];
      },
    });
  }

  private load() {
    const today = formatDateAsYmd(new Date());
    const horizon = plusDays(today, UPCOMING_DAYS);
    this.dateExclusionService.listNamespace('ordering').subscribe({
      next: result => {
        this.lines = (result?.data || [])
          .filter(
            (row: DateExclusionRow) => row.exclusion_start_date <= horizon,
          )
          .map((row: DateExclusionRow) => ({
            text: blackoutLabel(row, today),
            active: isBlackoutActive(row, today),
          }));
      },
      error: () => {
        this.lines = [];
      },
    });
  }
}

@NgModule({
  declarations: [OrderingBlackoutBannerComponent],
  imports: [CommonModule],
  exports: [OrderingBlackoutBannerComponent],
})
export class OrderingBlackoutBannerModule {}
